// Verdict-reason vocabulary shared by the verdict layer (backend) and the run
// UI (frontend). Kept here so the symptom code and its display rule have exactly
// one owner — neither side hard-codes the magic string or re-derives the order.

import type { RunItemVerdictReason, RunReasonCohortTally } from "./schemas"

// ── Gap-reason vocabulary ────────────────────────────────────────────────────
//
// Constant-first (same pattern as REASON_CLASS below): the single owner of every
// gap/drop reason code the pipeline emits. Each emitter types its local reason
// union from these members and the readiness compiler keys its reason→owner map
// with them, so a typo'd or renamed code fails the build instead of silently
// routing a defect to the `unknown.unclassified` backstop. Grouped by the layer
// that emits the code.
export const GAP_REASON = {
  // Feed-compiler build gaps (feeds/build-csv.ts)
  unknownList: "unknown_list",
  // Nova holds no cached marketplace definition of the attribute for the row's
  // category (or any of its ancestors, or as a global). Without a definition
  // there is no type and no value list to validate against, so the value cannot
  // be translated or checked — it is gapped instead of shipped raw.
  unknownAttribute: "unknown_attribute",
  unmappedLabel: "unmapped_label",
  operatorCodeInWriter: "operator_code_in_writer",
  missingRequired: "missing_required",
  nonNumeric: "non_numeric",
  categoryNotLeaf: "category_not_leaf",
  duplicateVariant: "duplicate_variant",
  variantDifferentiatorMissing: "variant_differentiator_missing",
  variantTitleDuplicate: "variant_title_duplicate",
  // Canonical writer gaps.
  aiRequired: "ai_required",
  manualRequired: "manual_required",
  missing: "missing",
  lookupMiss: "lookup_miss",
  cycle: "cycle",
  // Whole-SKU synthetic view gaps (attribute-mapping/gap-classification.ts)
  notTracked: "not_tracked",
  noCategory: "no_category",
  noWriter: "no_writer",
  notInCatalogMirror: "not_in_catalog_mirror",
  noEligibleVariants: "no_eligible_variants",
  oekoTexNotCertified: "oeko_tex_not_certified",
  oekoTexEvidenceMissing: "oeko_tex_evidence_missing",
  oekoTexEvidenceConflict: "oeko_tex_evidence_conflict",
  clothingScopeUnknown: "clothing_scope_unknown",
  clothingArtifactMismatch: "clothing_artifact_mismatch",
  // Value-store read path (#232 slice C). `no_materialized_values` is the
  // value-backed twin of `no_active_writer`: the config has no content for this
  // marketplace and the fix is to materialize it. `values_stale` is the
  // build-side freshness refusal — the persisted values cannot be proven to
  // match the inputs a writer would read now, so the build refuses instead of
  // shipping content nobody can vouch for.
  noMaterializedValues: "no_materialized_values",
  valuesStale: "values_stale",
  // Whole-variant feed-transform row drops (feeds/pipeline.ts)
  nutritionTableMissing: "nutrition_table_missing",
  sizeCpnUnresolved: "size_cpn_unresolved",
  // Run precondition blocker (listing-runs/preconditions.ts)
  noFacts: "no_facts",
  // Final platform-artifact validation. These reasons are emitted after the
  // generic feed rows are complete, when the registered marketplace adapter
  // checks its exact outbound contract. They stay in this shared vocabulary so
  // validate, diagnose, heal, and the durable build classify the same codes.
  missingProductField: "missing_product_field",
  fieldTooLong: "field_too_long",
  invalidCopyContent: "invalid_copy_content",
  invalidArticleId: "invalid_article_id",
  multipleProducts: "multiple_products",
  articleIdCollision: "article_id_collision",
  unknownCategory: "unknown_category",
  categoryDetailMissing: "category_detail_missing",
  ambiguousCategoryId: "ambiguous_category_id",
  invalidPriority: "invalid_priority",
  enumSchemaMissing: "enum_schema_missing",
  unknownEnumValue: "unknown_enum_value",
  unknownParameter: "unknown_parameter",
  missingRequiredParameter: "missing_required_parameter",
  invalidMediaUrl: "invalid_media_url",
  invalidEan: "invalid_ean",
  missingPrice: "missing_price",
  tooManyVariableParameters: "too_many_variable_parameters",
  duplicateVariantAxes: "duplicate_variant_axes",
  missingVat: "missing_vat",
  inconsistentVat: "inconsistent_vat",
  adapterConfig: "config",
  emptySelection: "empty_selection",
  artifactTooLarge: "artifact_too_large",
  tooManyProducts: "too_many_products",
  invalidGtin: "invalid_gtin",
  invalidUrl: "invalid_url",
} as const

export type GapReason = (typeof GAP_REASON)[keyof typeof GAP_REASON]

// The verdict reason for "all variants filtered by eligibility". It's a symptom,
// not a cause — its real causes (brand/price/EAN/…) are separate reasons on the
// same config — so the UI hides it and the run summary never tallies it.
export const NO_ELIGIBLE_VARIANTS_REASON = GAP_REASON.noEligibleVariants

/** The verdict reasons to actually show for a NOK config, in render order. Drops
 *  the `no_eligible_variants` symptom so badges/chips surface only the real
 *  causes beside it, then orders fixable (gap) before won't-list (eligibility).
 *  Falls back to the original reasons when the symptom is the only one, so a
 *  config never renders with zero reasons. */
export function displayVerdictReasons(
  reasons: readonly RunItemVerdictReason[]
): RunItemVerdictReason[] {
  const withoutSymptom = reasons.filter((reason) => reason.reason !== NO_ELIGIBLE_VARIANTS_REASON)
  const shown = withoutSymptom.length > 0 ? withoutSymptom : [...reasons]
  return shown.sort((a, b) => Number(b.fixable) - Number(a.fixable))
}

// ── Reason classification (reconciliation) ───────────────────────────────────
//
// An axis ORTHOGONAL to `kind`/`fixable`: does a reason describe a config the
// marketplace *legitimately* shouldn't carry, or a data/pipeline gap that could
// be hiding a product we *should* be listing? The run-reconciliation layer uses
// this to answer "is every CZ↔PL difference explained, or is something silently
// dropping products?" — a `legit` difference is the system working correctly; a
// `suspect` one warrants investigation before you trust the run.
//
//   - `legit`   — an intended won't-list: a deliberate policy/eligibility gate
//                 (price floor, country-version, banned, invalid EAN, plus
//                 historical bulk-pack verdicts persisted before issue #650).
//   - `suspect` — a data or pipeline gap: missing catalog row, missing price,
//                 unmapped category, no writer, unbuilt required attribute. These
//                 *look* like exclusions but mean "something upstream is missing".
//
// Unknown reasons default to `suspect`: for a completeness audit, "I don't
// recognise this" should bias toward investigate, never toward silently fine.
//
// Constant-first: the values live in one frozen object and the type is inferred
// from it, so call sites reference `REASON_CLASS.suspect` (a checked member)
// instead of re-typing the bare `"suspect"` string literal.
export const REASON_CLASS = {
  legit: "legit",
  suspect: "suspect",
} as const

export type ReasonClass = (typeof REASON_CLASS)[keyof typeof REASON_CLASS]

// Exact-match reasons that are a deliberate, correct reason not to list. The
// `price_below_<n>` reason is handled by prefix below (the threshold varies).
export const LEGIT_REASON_CODES = [
  "oeko_tex_not_certified",
  "brand_not_allowed",
  "source_category_excluded",
  "locally_banned",
  "long_term_unavailable",
  "delisted_out_of_stock",
  "country_version_mismatch",
  // Historical persisted verdict. Current eligibility no longer emits it.
  "bulk_pack_only",
  "ean_invalid",
  // Never-listable product class (sample / bundle), excluded by the config-driven
  // eligibility rule. A deliberate gate, not a data gap — see eligibility.ts.
  "sample_or_bundle",
  "prohibited_peptide",
] as const
const LEGIT_REASONS = new Set<string>(LEGIT_REASON_CODES)

/** Classify a single verdict-reason code. Pass the bare `reason` string (e.g.
 *  `"price_below_75"`, `"no_active_writer"`), not the whole RunItemVerdictReason.
 *  Callers should drop the `no_eligible_variants` symptom first (it's an
 *  aggregate, not a cause) via `displayVerdictReasons`; classified directly it
 *  falls through to `suspect`. */
export function reasonClass(reason: string): ReasonClass {
  if (reason.startsWith("price_below_")) {
    return REASON_CLASS.legit
  }
  if (LEGIT_REASONS.has(reason)) {
    return REASON_CLASS.legit
  }
  return REASON_CLASS.suspect
}

/** True when at least one of a config's reasons is a suspect data/pipeline gap —
 *  i.e. the config's exclusion may be hiding a product we should be listing.
 *  Ignores the `no_eligible_variants` symptom so a config flagged solely by its
 *  real (legit) causes is not mislabelled suspect. A bare symptom has no
 *  proven business cause, so it remains suspect until explained. */
export function hasSuspectReason(reasons: readonly RunItemVerdictReason[]): boolean {
  const causes = reasons.filter((reason) => reason.reason !== NO_ELIGIBLE_VARIANTS_REASON)
  return (
    (reasons.length > 0 && causes.length === 0) ||
    causes.some((reason) => reasonClass(reason.reason) === REASON_CLASS.suspect)
  )
}

/** True when a config carries at least one fixable gap — an actionable
 *  data/pipeline issue (missing attr, category-not-leaf, …) as opposed to an
 *  intended won't-list eligibility exclusion. Ignores the `no_eligible_variants`
 *  symptom (its fixable causes are separate reasons). This is the single owner of
 *  the "has a fixable cause" predicate: both the run summary's `nokFixable` tally
 *  and the run page's "Gaps only" filter call it, so the count and the filtered
 *  set can never drift. */
export function hasFixableGap(reasons: readonly RunItemVerdictReason[]): boolean {
  return reasons.some((r) => r.reason !== NO_ELIGIBLE_VARIANTS_REASON && r.fixable)
}

/** Feed-compiler defect reasons owned by the writer / enum / schema layer that an
 *  LLM re-extract provably CANNOT fix: a writer that collapses distinct variants
 *  into one value (`duplicate_variant`), an emitted value that is not an exact
 *  value-list member or is a raw operator code (`unmapped_label`,
 *  `operator_code_in_writer`), and marketplace schema/value-list drift
 *  (`unknown_list`, `unknown_attribute`, `non_numeric`). Single owner of this
 *  subset so the two sides that must agree can never drift (issue #114):
 *  `nova runs heal` refuses to route these to extract and delegates to
 *  `nova runs diagnose`, and the readiness compiler maps each to its
 *  writer/enum/schema root-cause owner. */
export const WRITER_COMPILER_REASONS: ReadonlySet<string> = new Set([
  GAP_REASON.duplicateVariant,
  GAP_REASON.unmappedLabel,
  GAP_REASON.operatorCodeInWriter,
  GAP_REASON.unknownList,
  GAP_REASON.unknownAttribute,
  GAP_REASON.nonNumeric,
  GAP_REASON.tooManyVariableParameters,
  GAP_REASON.duplicateVariantAxes,
  GAP_REASON.categoryDetailMissing,
  GAP_REASON.ambiguousCategoryId,
  GAP_REASON.enumSchemaMissing,
  GAP_REASON.unknownEnumValue,
  GAP_REASON.unknownParameter,
])

/** True when a gap reason is a writer/enum/schema compiler defect (see
 *  `WRITER_COMPILER_REASONS`) — the heal router uses it to refuse re-extract. */
export function isWriterCompilerReason(reason: string): boolean {
  return WRITER_COMPILER_REASONS.has(reason)
}

/** True when a NOK config would become listable once its gaps are filled: it has
 *  at least one real reason and EVERY real reason is fixable, so no hard
 *  won't-list eligibility blocker remains. Stricter than hasFixableGap (which
 *  only needs one fixable reason): a config that's fixable AND also price-blocked
 *  is not recoverable, because filling the gap still leaves the price blocker.
 *  Ignores the `no_eligible_variants` symptom (its real causes are separate
 *  reasons). This owns the "will list once filled" predicate behind the run
 *  summary's `nokRecoverable` tally. */
export function isRecoverable(reasons: readonly RunItemVerdictReason[]): boolean {
  const real = reasons.filter((r) => r.reason !== NO_ELIGIBLE_VARIANTS_REASON)
  return real.length > 0 && real.every((r) => r.fixable)
}

/** The NOK cohort split + reason histogram over a set of NON-parked NOK configs.
 *  `fixable + blocked` equals the number of configs passed; `recoverable` is a
 *  subset of `fixable`. `reasons` is the per-reason histogram with the
 *  config-cohort split (fixableCount / excludedCount), already sorted. */
export type NokReasonTally = {
  fixable: number
  blocked: number
  recoverable: number
  reasons: RunReasonCohortTally[]
}

/**
 * The single NOK-reason aggregation used by the per-run summary
 * (`getRunSkuSummary`).
 *
 * Pass the verdict-reason arrays of the NON-parked NOK configs only — the
 * set-aside cohort is filtered and counted by the caller since it is orthogonal
 * to this reason aggregation.
 *
 *   - A config is Fixable when `hasFixableGap` (≥1 fixable gap), else Blocked
 *     (pure won't-list). `recoverable` is the stricter subset whose EVERY real
 *     reason is fixable (`isRecoverable`).
 *   - Every reason is tallied into the CONFIG's cohort column (fixableCount /
 *     excludedCount), NOT the reason's own `fixable` flag, so each facet count
 *     equals the `cohort=… & reason=…` grid with zero drift.
 *   - `no_eligible_variants` is a symptom, not a cause — dropped from the
 *     histogram (its real causes are separate reasons on the same config).
 *   - Reasons are sorted Fixable-first, then by frequency — the order run views
 *     render the chips in, decided once here.
 */
export function tallyNokReasons(
  configs: readonly (readonly RunItemVerdictReason[])[]
): NokReasonTally {
  const byReason = new Map<string, RunReasonCohortTally>()
  let fixable = 0
  let blocked = 0
  let recoverable = 0
  for (const verdictReasons of configs) {
    const configFixable = hasFixableGap(verdictReasons)
    for (const reason of verdictReasons) {
      if (reason.reason === NO_ELIGIBLE_VARIANTS_REASON) {
        continue
      }
      let existing = byReason.get(reason.reason)
      if (!existing) {
        existing = {
          reason: reason.reason,
          fixable: reason.fixable,
          count: 0,
          fixableCount: 0,
          excludedCount: 0,
        }
        byReason.set(reason.reason, existing)
      }
      existing.count += 1
      // Bucket by the CONFIG's cohort, not the reason's own `fixable` flag.
      if (configFixable) {
        existing.fixableCount += 1
      } else {
        existing.excludedCount += 1
      }
    }
    if (configFixable) {
      fixable += 1
    } else {
      blocked += 1
    }
    if (isRecoverable(verdictReasons)) {
      recoverable += 1
    }
  }
  const reasons = [...byReason.values()].sort(
    (a, b) => Number(b.fixable) - Number(a.fixable) || b.count - a.count
  )
  return { fixable, blocked, recoverable, reasons }
}
